import React from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import {Icon} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * A data table. Desktop only, on purpose.
 *
 * The first desktop pass widened the phone's cards and called it a layout. A card is
 * an object you look AT: a face, a name, a badge, 90pt tall with air around it. On a
 * 1440pt window that meant eight people on screen where a table shows forty, and the
 * columns a manager actually scans — department, whether a face is enrolled, when they
 * arrived — were not columns at all, so they could not be compared down the page.
 *
 * So: a real table. Fixed header, rows on a 44pt rhythm, hover, a selected row that
 * stays marked while its detail is open beside it (see SplitView). Numbers and times
 * render mono so they line up digit-for-digit down a column, which is the whole reason
 * to put them in one.
 *
 * The phone keeps its cards. This component simply is not mounted there — a table on a
 * 390pt screen is a horizontal scroll, and a horizontal scroll is a lost user.
 *
 * ## The grid's own surfaces
 *
 * Это `Table` ui-kit: шапка 28pt на `surface-inset` с плотной линией под ней,
 * колонки разделены линиями только в шапке; строки чередуются заливкой — в плотной
 * таблице глаз держит строку по заливке, а не по горизонтальной линии; наведение —
 * `surface-hover`; выбранная строка — `surface-active` плюс акцентная полоса у
 * левого края: заливка одна сливается с наведением, полоса — нет.
 */
export type Column<T> = {
  key: string;
  /** Column heading. Comes from i18n at the call site. */
  title: string;
  /** Flex weight; omit for a fixed `width`. */
  flex?: number;
  width?: number;
  align?: 'left' | 'right';
  /** Cell body. Return a string for the default text treatment, or a node. */
  render: (row: T) => React.ReactNode;
  /** Drop this column below a given window width — the first to go when space runs out. */
  minWidth?: number;
  /**
   * Makes the header a sort control. The value is opaque to the table and handed
   * straight back through `sort.onChange` — callers key their own comparator off it.
   */
  sortKey?: string;
};

/**
 * Wiring for sortable headers. A table without this renders exactly as before; with
 * it, every column that declares a `sortKey` becomes pressable.
 *
 * Sorting is the caller's job, not the table's: a report sorts a copy of its
 * react-query data (see features/view-attendance/lib/aggregate), and a table that
 * sorted internally would either mutate that cache or hold a second copy of it.
 */
export type TableSort = {
  key: string;
  direction: 'asc' | 'desc';
  /** Pressing the ACTIVE column's header should flip direction; that is the caller's. */
  onChange: (key: string) => void;
};

const styles = StyleSheet.create({
  /* Сетка в рамке `border-default`, и ничего больше — ни тени, ни скругления карточки.
     Таблица — не карточка, это тело страницы. */
  root: {borderRadius: radii.md, borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden'},
  /* Inside a Panel the grid has no chrome of its own: the panel is the card, and a
     second border, radius and shadow 1pt inside the first reads as a seam. */
  /* Растёт по содержимому: коробку во всю высоту оно заполняло затем, чтобы внутри
     себя прокручиваться, а прокрутки у него больше нет. */
  docked: {minHeight: 0},
  dockedBody: {paddingBottom: spacing(2)},
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingHorizontal: spacing(3),
    height: 28,
    borderBottomWidth: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingHorizontal: spacing(3),
    minHeight: 36,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  // The last row's rule would double up with the container's own border.
  lastRow: {borderBottomWidth: 0},
  headCell: {flexDirection: 'row', alignItems: 'center', gap: 4},
  headCellRight: {justifyContent: 'flex-end'},
});

/**
 * A cell's own box: its share of the row, and nothing outside it. The
 * `overflow` is the backstop, not the mechanism — see CellBox.
 */
function cellStyle<T>(col: Column<T>): StyleProp<ViewStyle> {
  return {
    flex: col.width ? undefined : col.flex ?? 1,
    width: col.width,
    minWidth: 0,
    overflow: 'hidden',
    alignItems: col.align === 'right' ? 'flex-end' : 'flex-start',
  };
}

/**
 * The one thing between a cell's content and the column beside it.
 *
 * A cell is a column flex box aligned to its own edge (`flex-start`, or `flex-end` for
 * a numeric column), because a status pill has to hug its label rather than stretch
 * across 108pt of empty cell. But `align-items: flex-start` also means the child is
 * sized by its CONTENT — so a device called "Проходная 1" laid itself out 131pt wide
 * inside a 91pt cell and printed straight over the type column next to it.
 * `numberOfLines={1}` never fired: the text was not too wide for its box, its box was
 * too wide for the cell.
 *
 * `maxWidth: '100%'` is what caps it. The box is still content-sized — the pill still
 * hugs — but never wider than the cell, and everything inside it now has a real width
 * to truncate against. The `overflow: hidden` on the cell catches whatever a future
 * cell manages to overflow anyway: clipping is ugly, printing over the neighbouring
 * column is a bug report.
 */
function CellBox({children}: {children: React.ReactNode}) {
  return <View style={cellBoxStyle}>{children}</View>;
}

const cellBoxStyle: ViewStyle = {maxWidth: '100%', minWidth: 0};

export function DataTable<T>({
  columns,
  rows,
  keyOf,
  onRowPress,
  selectedKey,
  /** Window width, so narrow columns can drop out instead of crushing. */
  width,
  sort,
  docked = false,
  bare = false,
  style,
  testID,
}: {
  columns: Column<T>[];
  rows: T[];
  keyOf: (row: T) => string;
  onRowPress?: (row: T) => void;
  selectedKey?: string | null;
  width?: number;
  sort?: TableSort;
  /**
   * The grid lives inside a `Panel`: it drops its own border, radius, shadow and
   * gradient. This is the console's shape — a grid there is never a card with lines in
   * it, it is the body of the panel it is docked in.
   *
   * Прокрутку оно себе больше не берёт: на странице ровно одна полоса, и она у
   * страницы. См. комментарий у тела ниже.
   */
  docked?: boolean;
  /**
   * The grid sits inside a `Panel` that GROWS with the page rather than filling a box:
   * no chrome of its own and no scroll of its own, so the page scrolls it. Use this for
   * a short grid on a board; use `docked` for the full-height one on a list screen.
   */
  bare?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const visible = columns.filter(c => !c.minWidth || !width || width >= c.minWidth);

  const head = (
    <View style={[styles.head, {backgroundColor: p.gridHeader, borderBottomColor: p.borderStrong}]}>
      {visible.map(col => {
        const sortable = sort && col.sortKey;
        const active = sortable && sort.key === col.sortKey;
        const heading = (
          <View
            style={[
              styles.headCell,
              col.align === 'right' && styles.headCellRight,
            ]}>
            {/* `text-xs font-medium text-muted-foreground`, sentence case — shadcn's
                `TableHead`. It used to be tracked capitals; Cyrillic caps have no
                ascender silhouette to scan by, and a head row is read as words. */}
            <AppText
              variant="eyebrow"
              color={active ? p.primaryInk : p.textSecondary}
              numberOfLines={1}>
              {col.title}
            </AppText>
            {active ? (
              <Icon
                name={sort.direction === 'asc' ? 'chevronUp' : 'chevronDown'}
                size={12}
                color={p.primaryInk}
              />
            ) : null}
          </View>
        );

      return (
        <View key={col.key} style={cellStyle(col)}>
          <CellBox>
            {sortable ? (
              <Pressable
                accessibilityRole="button"
                accessibilityState={{selected: !!active}}
                accessibilityLabel={col.title}
                onPress={() => sort.onChange(col.sortKey as string)}
                testID={`sort-${col.sortKey}`}
                hitSlop={6}>
                {heading}
              </Pressable>
            ) : (
              heading
            )}
          </CellBox>
        </View>
      );
      })}
    </View>
  );

  const body = rows.map((row, i) => {
    const key = keyOf(row);
    const selected = selectedKey === key;
    const last = i === rows.length - 1;

    const cells = visible.map(col => (
      <View key={col.key} style={cellStyle(col)}>
        <CellBox>{col.render(row)}</CellBox>
      </View>
    ));

    // Чередование строк: в плотной таблице глаз держит строку по заливке.
    const zebra = i % 2 === 1 ? p.inset : 'transparent';

    if (!onRowPress) {
      return (
        <View
          key={key}
          style={[
            styles.row,
            {borderBottomColor: p.borderSubtle, backgroundColor: zebra},
            last && styles.lastRow,
          ]}>
          {cells}
        </View>
      );
    }

    return (
      <Pressable
        key={key}
        testID={`row-${key}`}
        accessibilityRole="button"
        accessibilityState={{selected}}
        onPress={() => onRowPress(row)}
        style={state => [
          styles.row,
          {borderBottomColor: p.borderSubtle, backgroundColor: zebra},
          last && styles.lastRow,
          // Hover is what tells a pointer the row is live; selection is what keeps
          // it marked once its detail is open beside it. They must not look alike —
          // two greys one step apart cannot be told apart, so selection also carries
          // a rail of the accent down the left edge.
          isHot(state) && !selected && {backgroundColor: p.accent},
          selected && {
            backgroundColor: p.active,
            borderLeftWidth: 3,
            borderLeftColor: p.primary,
            paddingLeft: spacing(3) - 3,
          },
        ]}>
        {cells}
      </Pressable>
    );
  });

  /*
    ОДНА полоса прокрутки на страницу — у страницы.

    Раньше сетка держала свою: пристыкованная катала строки под неподвижной шапкой, а
    карточная получала потолок в 660pt и полосу внутри себя. Обе честно решали задачу
    «сорок строк не должны уносить с экрана всё остальное» и обе создавали то, что
    хуже решаемой задачи, — вложенную прокрутку. Колесо над таблицей крутит её, колесо
    в двух сантиметрах правее крутит страницу; докрутив таблицу до низа, человек ждёт
    продолжения страницы и не получает его, пока не уведёт курсор. На тачпаде это ещё
    и перехват инерции.

    Поэтому тело просто растёт, а до нужной строки доводят те же средства, что и
    раньше: поиск, отбор и рейка страниц под сеткой — постранично сюда приходит
    двадцать пять строк, а не сорок тысяч.
  */
  if (bare) {
    return (
      <View testID={testID} style={style}>
        {head}
        {body}
      </View>
    );
  }

  if (docked) {
    return (
      <View testID={testID} style={[styles.docked, style]}>
        {head}
        <View style={styles.dockedBody}>{body}</View>
      </View>
    );
  }

  return (
    <View
      testID={testID}
      style={[styles.root, {backgroundColor: p.card, borderColor: p.border}, style]}>
      {head}
      {body}
    </View>
  );
}

/** True while the pointer is over the row, or a finger is down on it. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * A cell that leads with a mark — an avatar, a device glyph — and then a name.
 *
 * It is a component rather than a StyleSheet each list copies, because of the two
 * properties below. Without `flex: 1` the row sizes to its CONTENT and grows past the
 * column it was given; without `flexShrink` on the label, the label does the same
 * inside the row. Either one missing and a long name prints over the column beside it
 * instead of truncating — which is what it did on the device list, in both copies of
 * this markup.
 */
export function NameCell({
  mark,
  children,
  detail,
}: {
  /**
   * The avatar / glyph that leads the row. Sized by the caller.
   *
   * Optional: a grid whose rows are not people at all — or one drawn without a company
   * to fetch faces from — wants the same truncation and the same typography with no
   * mark. Omitted, nothing is rendered and the row's `gap` has nothing to separate.
   */
  mark?: React.ReactNode;
  children: React.ReactNode;
  /**
   * A second, quieter line under the name — the identifier that does not change when
   * the name does (a terminal's machine number). Monospaced, so a code reads as a code.
   */
  detail?: React.ReactNode;
}) {
  return (
    <View style={nameCellStyles.row}>
      {mark}
      <View style={nameCellStyles.label}>
        <AppText variant="subtitle" numberOfLines={1}>
          {children}
        </AppText>
        {detail ? (
          <AppText variant="mono" numberOfLines={1} style={nameCellStyles.detail}>
            {detail}
          </AppText>
        ) : null}
      </View>
    </View>
  );
}

const nameCellStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    flex: 1,
    minWidth: 0,
  },
  label: {flexShrink: 1, minWidth: 0, gap: 1},
  detail: {opacity: 0.7},
});

/** The default cell: one line of body text that truncates rather than wrapping. */
export function Cell({
  children,
  tone,
  mono = false,
}: {
  children: React.ReactNode;
  tone?: string;
  mono?: boolean;
}) {
  return (
    <AppText variant={mono ? 'mono' : 'body'} color={tone} numberOfLines={1}>
      {children}
    </AppText>
  );
}
